const hasValues = (value) => Array.isArray(value) && value.length > 0

const toISOString = (value) => {
  if (!value) return undefined
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString()
}

const normalizeHealthyState = (value) => {
  return typeof value === 'boolean' ? value : undefined
}

const normalizeOrderBy = (orderBy) => {
  if (!hasValues(orderBy)) return undefined

  return orderBy.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
}

const normalizePhoto = (photo, questionId, photoIndex) => {
  const source =
    typeof photo === 'string'
      ? photo
      : photo?.url ?? photo?.file_url ?? photo?.filebase64 ?? photo?.fileBase64
  if (!source) return null

  return {
    id: `${questionId}-photo-${photoIndex}`,
    name: photo?.filename ?? photo?.file_name ?? `foto-${photoIndex + 1}.jpg`,
    url: /^(data:|https?:|blob:)/.test(source)
      ? source
      : `data:image/jpeg;base64,${source}`,
  }
}

export const buildVehicleInspectionsInput = ({ tableFilter = {}, activeFilters = {} } = {}) => {
  const submittedAtStart = toISOString(activeFilters.submittedAtStart)
  const submittedAtEnd = toISOString(activeFilters.submittedAtEnd)
  const input = {
    pagination: {
      page: Number(tableFilter.pagination?.page) || 1,
      show: Number(tableFilter.pagination?.show) || 10,
    },
  }

  if (hasValues(activeFilters.inspectionIds)) input.inspectionIds = activeFilters.inspectionIds
  if (hasValues(activeFilters.vehicleIds)) input.vehicleIds = activeFilters.vehicleIds
  if (hasValues(activeFilters.inspectorIds)) input.inspectorIds = activeFilters.inspectorIds

  const isHealthy = normalizeHealthyState(activeFilters.isHealthy)
  if (isHealthy !== undefined) input.isHealthy = isHealthy
  if (tableFilter.search?.trim()) input.search = tableFilter.search.trim()

  const orderBy = normalizeOrderBy(tableFilter.orderBy)
  if (orderBy) input.orderBy = orderBy

  if (submittedAtStart || submittedAtEnd) {
    input.submittedAt = {
      ...(submittedAtStart && { start: submittedAtStart }),
      ...(submittedAtEnd && { end: submittedAtEnd }),
    }
  }

  return input
}

export const mapVehicleInspectionResultToTableRow = (inspection) => ({
  inspectionId: inspection.inspectionId,
  companyId: inspection.companyId,
  templateId: inspection.templateId,
  versionId: inspection.versionId,
  documentNumber: inspection.code ?? '-',
  templateVersion: inspection.templateVersion?.snapshot,
  currentTemplateVersion: inspection.templateVersion?.current,
  templateName: inspection.templateName?.snapshot ?? '-',
  currentTemplateName: inspection.templateName?.current ?? '-',
  healthyState: inspection.isHealthy,
  vehicleId: inspection.vehicleId,
  driverId: inspection.inspectorId,
  inspectorId: inspection.inspectorId,
  submittedLicensePlate: inspection.licensePlate?.snapshot ?? '-',
  currentLicensePlate: inspection.licensePlate?.current ?? '-',
  inspectorName: inspection.inspectorName?.snapshot ?? '-',
  currentInspectorName: inspection.inspectorName?.current ?? '-',
  submittedAt: inspection.submittedAt,
  stats: {
    totalQuestion: inspection.stats?.totalQuestion ?? 0,
    healthy: inspection.stats?.totalHealthyAnswer ?? 0,
    unhealthy: inspection.stats?.totalUnhealthyAnswer ?? 0,
    emptyAnswer: inspection.stats?.totalEmptyAnswer ?? 0,
  },
})

export const getVehicleInspectionTemplateRoute = (
  inspection = {},
  { canUpdate = false } = {},
) => {
  const isCurrentVersion =
    Number(inspection.templateVersion) === Number(inspection.currentTemplateVersion)
  const isTemplateDeleted = inspection.isTemplateDeleted === true

  if (isCurrentVersion && !isTemplateDeleted && canUpdate) {
    return {
      name: 'Update Setting Template Vehicle Inspection',
      params: { templateId: inspection.templateId },
      query: { inspectionId: inspection.inspectionId },
    }
  }

  return {
    name: 'View Setting Template Vehicle Inspection',
    query: {
      inspectionId: inspection.inspectionId,
      templateId: inspection.templateId,
      versionNumber: inspection.templateVersion,
    },
  }
}

export const normalizeVehicleInspectionsResponse = (response) => {
  const inspectionsResult =
    response?.vehicleInspections?.vehicleInspections ??
    response?.data?.vehicleInspections?.vehicleInspections

  return {
    data: (inspectionsResult?.data ?? []).map(mapVehicleInspectionResultToTableRow),
    pagination: inspectionsResult?.pagination ?? {},
  }
}

export const buildVehicleInspectionInput = (inspectionId) => ({
  inspectionId: Number(inspectionId),
})

export const normalizeVehicleInspectionResponse = (response) => {
  const inspection =
    response?.vehicleInspection?.vehicleInspection ??
    response?.data?.vehicleInspection?.vehicleInspection
  if (!inspection) return null

  return {
    inspectionId: inspection.inspectionId,
    companyId: inspection.companyId,
    templateId: inspection.templateId,
    versionId: inspection.versionId,
    documentNumber: inspection.code ?? '-',
    isTemplateDeleted: inspection.isTemplateDeleted === true,
    templateVersion: inspection.templateVersion?.snapshot,
    currentTemplateVersion: inspection.templateVersion?.current,
    templateName: inspection.templateName?.snapshot ?? '-',
    currentTemplateName: inspection.templateName?.current ?? '-',
    healthyState: inspection.isHealthy,
    vehicleId: inspection.vehicleId,
    submittedLicensePlate: inspection.licensePlate?.snapshot ?? '-',
    currentLicensePlate: inspection.licensePlate?.current ?? '-',
    inspectorId: inspection.inspectorId,
    inspectorName: inspection.inspectorName?.snapshot ?? '-',
    currentInspectorName: inspection.inspectorName?.current ?? '-',
    submittedAt: inspection.submittedAt,
    stats: {
      totalQuestion: inspection.stats?.totalQuestion ?? 0,
      healthy: inspection.stats?.totalHealthyAnswer ?? 0,
      unhealthy: inspection.stats?.totalUnhealthyAnswer ?? 0,
      emptyAnswer: inspection.stats?.totalEmptyAnswer ?? 0,
    },
    categories: (inspection.sections ?? []).map((section, sectionIndex) => ({
      id: `section-${sectionIndex}`,
      name: section.name,
      isExpanded: sectionIndex === 0,
      questions: (section.questions ?? []).map(({ question, answer }, questionIndex) => {
        const questionId = answer?.questionId ?? `section-${sectionIndex}-question-${questionIndex}`
        const attachments = (answer?.photos ?? [])
          .map((photo, photoIndex) => normalizePhoto(photo, questionId, photoIndex))
          .filter(Boolean)
        const hasHealthyAnswer = typeof answer?.isHealthy === 'boolean'
        const hasHealthyValueAnswer =
          answer?.isHealthyValue !== null &&
          answer?.isHealthyValue !== undefined &&
          String(answer.isHealthyValue).length > 0
        const hasTextAnswer =
          answer?.textValue !== null &&
          answer?.textValue !== undefined &&
          String(answer.textValue).length > 0
        const totalPhoto = Math.max(Number(answer?.totalPhoto) || 0, attachments.length)
        const hasPhotoAnswer = totalPhoto > 0
        const hasAnswer = Boolean(
          answer &&
            (hasHealthyAnswer || hasHealthyValueAnswer || hasTextAnswer || hasPhotoAnswer),
        )

        return {
          id: questionId,
          title: question,
          isHealthyValue: hasHealthyValueAnswer ? String(answer.isHealthyValue) : '',
          textValue: hasTextAnswer ? String(answer.textValue) : '',
          hasAnswer,
          isHealthy: hasHealthyAnswer ? answer.isHealthy : null,
          isCritical: false,
          note: answer?.note ?? '',
          totalPhoto,
          attachments,
        }
      }),
    })),
  }
}
